import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Add, People } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import { formatAppTimestamp } from '../../utils/dateTimeUtils';

const sidebarAwareDialogSx = (maxPx: number) => ({
  zIndex: (theme: { zIndex: { modal: number } }) => theme.zIndex.modal + 6,
  '& .MuiDialog-container': {
    paddingLeft: '16px',
    paddingRight: '16px',
    boxSizing: 'border-box',
  },
  '& .MuiDialog-paper': {
    width: `min(${maxPx}px, calc(100vw - clamp(172px, 13.4375vw, 280px) - 48px))`,
    maxWidth: `min(${maxPx}px, calc(100vw - clamp(172px, 13.4375vw, 280px) - 48px))`,
    margin: 'auto',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
});

const Licenses: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [licenseData, setLicenseData] = useState<{
    totalLicenses: number;
    usedLicenses: number;
    availableLicenses: number;
    planType: string;
    displayPlan?: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [purchasing, setPurchasing] = useState(false);
  const [licenseCount, setLicenseCount] = useState(0);
  const [prorationPreview, setProrationPreview] = useState<{
    originalPrice: number;
    proratedPricePerLicense: number;
    totalAmount: number;
    remainingBillingPeriodDays: number;
    billingCycle?: string;
  } | null>(null);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [history, setHistory] = useState<Array<{
    id: number;
    changedAt: string;
    previousCount: number;
    newCount: number;
    delta: number;
    reason?: string;
    changedByUserId?: number | null;
    changedByUserName?: string | null;
    changedByUserEmail?: string | null;
  }> | null>(null);

  // Auto-hide success message after 10 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 10000); // 10 seconds

      return () => clearTimeout(timer);
    }
  }, [success]);

  // Auto-hide error message after 10 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 10000); // 10 seconds

      return () => clearTimeout(timer);
    }
  }, [error]);

  // Load data on mount (skip when returning from payment — refresh effect handles that).
  useEffect(() => {
    if (searchParams.get('refresh') === 'true') {
      return;
    }
    void loadLicenseUsage();
    void loadLicenseHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle refresh parameter when returning from payment — apply count immediately, then confirm from API.
  useEffect(() => {
    if (searchParams.get('refresh') !== 'true') {
      return;
    }

    let cancelled = false;

    const applyOptimisticFromPayment = () => {
      try {
        const raw = sessionStorage.getItem('manageAccount:licensePurchaseApplied');
        if (!raw) return;
        const parsed = JSON.parse(raw) as {
          newCount?: number | null;
          additionalLicenses?: number | null;
          at?: number;
        };
        sessionStorage.removeItem('manageAccount:licensePurchaseApplied');
        if (parsed?.newCount != null && Number.isFinite(Number(parsed.newCount))) {
          const total = Math.max(1, Number(parsed.newCount));
          setLicenseData((prev) => {
            const used = prev?.usedLicenses ?? 0;
            return {
              totalLicenses: total,
              usedLicenses: used,
              availableLicenses: Math.max(0, total - used),
              planType: prev?.planType || 'FREE',
              displayPlan: prev?.displayPlan || prev?.planType || 'FREE',
            };
          });
        }
      } catch {
        /* ignore */
      }
    };

    const run = async () => {
      applyOptimisticFromPayment();
      setSuccess('License purchase completed! Your licenses have been updated.');
      await loadLicenseUsage();
      void loadLicenseHistory();
      if (cancelled) return;
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('refresh');
      setSearchParams(newParams, { replace: true });
    };

    void run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams]);

  const loadProrationPreview = useCallback(async (quantity: number) => {
    if (quantity <= 0) {
      return;
    }
    if (licenseData?.planType !== 'STANDARD' && licenseData?.planType !== 'PROFESSIONAL') {
      return;
    }
    try {
      setLoadingPrice(true);
      const response = await apiService.getLicenseProrationPreview(quantity);
      if (response.error) {
        setError(response.error);
        setProrationPreview(null);
      } else if (
        response.originalPrice != null &&
        response.proratedPricePerLicense != null &&
        response.totalAmount != null
      ) {
        setProrationPreview({
          originalPrice: response.originalPrice,
          proratedPricePerLicense: response.proratedPricePerLicense,
          totalAmount: response.totalAmount,
          remainingBillingPeriodDays: response.remainingBillingPeriodDays ?? 0,
          billingCycle: response.billingCycle,
        });
        setError(null);
      }
    } catch (err: any) {
      console.error('Failed to load license proration preview:', err);
      setError(err.response?.data?.error || 'Failed to load prorated pricing');
      setProrationPreview(null);
    } finally {
      setLoadingPrice(false);
    }
  }, [licenseData?.planType]);

  useEffect(() => {
    if (purchaseDialogOpen && licenseCount > 0) {
      loadProrationPreview(licenseCount);
    }
  }, [purchaseDialogOpen, licenseCount, loadProrationPreview]);

  const loadLicenseHistory = async () => {
    try {
      const response = await apiService.getLicenseHistory();
      if (response.history) {
        setHistory(response.history);
      }
    } catch (err) {
      // History is non-critical; log to console only
      console.error('Failed to load license history', err);
    }
  };

  const loadLicenseUsage = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getLicenseUsage();
      if (response.error) {
        setError(response.error);
      } else {
        setLicenseData({
          totalLicenses: response.totalLicenses || 1,
          usedLicenses: response.usedLicenses || 0,
          availableLicenses: response.availableLicenses || 0,
          planType: response.planType || 'FREE',
          displayPlan: response.displayPlan || response.planType || 'FREE',
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load license usage');
    } finally {
      setLoading(false);
    }
  };

  // Keep license usage in sync when team members change in another tab.
  useEffect(() => {
    const handler = () => {
      // Don't block UI on history; count refresh is what matters for immediate feedback.
      void loadLicenseUsage();
      void loadLicenseHistory();
    };

    window.addEventListener('manageAccount:teamMembersChanged', handler);
    return () => {
      window.removeEventListener('manageAccount:teamMembersChanged', handler);
    };
  }, []);

  const handleAddLicense = () => {
    if (licenseData?.planType === 'ENTERPRISE') {
      setError('Enterprise plan includes unlimited licenses.');
      return;
    }
    setLicenseCount(1);
    setProrationPreview(null);
    setPurchaseDialogOpen(true);
    setError(null);
  };

  const handleResetLicenseCount = () => {
    setLicenseCount(0);
    setProrationPreview(null);
    setPurchaseDialogOpen(false);
    setError(null);
  };

  const handleProceedWithLicenseCount = async () => {
    if (licenseCount <= 0) {
      setError('Please add at least one license');
      return;
    }

    try {
      setPurchasing(true);
      setError(null);

      const response = await apiService.createLicenseCheckoutSession(licenseCount);
      if (!response.success || !response.checkoutUrl) {
        setError(response.message || 'Failed to start license checkout');
      } else {
        // Redirect directly to Stripe Checkout
        window.location.href = response.checkoutUrl;
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to start license checkout');
    } finally {
      setPurchasing(false);
      setLicenseCount(0);
      setProrationPreview(null);
    }
  };

  const formatRemainingPeriod = (days: number) => {
    if (days <= 0) {
      return 'Less than 1 day';
    }
    return days === 1 ? '1 day' : `${days} days`;
  };

  const usagePercentage = licenseData
    ? (licenseData.usedLicenses / licenseData.totalLicenses) * 100
    : 0;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', px: { xs: 2, sm: 3, md: 4 } }}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        License Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            License Overview
          </Typography>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <People />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Total Licenses</Typography>
              </Box>
              <Typography variant="h4">{licenseData?.totalLicenses || 0}</Typography>
              <Typography variant="body2" color="text.secondary">
                {licenseData?.displayPlan || licenseData?.planType || 'FREE'} Plan
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Used Licenses</Typography>
              <Typography variant="h4">{licenseData?.usedLicenses || 0}</Typography>
              <Typography variant="body2" color="text.secondary">
                Active team members
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Available Licenses</Typography>
              <Typography variant="h4" color={licenseData && licenseData.availableLicenses <= 0 ? 'error' : 'primary'}>
                {licenseData?.availableLicenses || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Remaining slots
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 2 }}>
            License Usage
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flexGrow: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={usagePercentage}
                  color={usagePercentage >= 90 ? 'error' : usagePercentage >= 70 ? 'warning' : 'primary'}
                  sx={{ height: 10, borderRadius: 5 }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {Math.round(usagePercentage)}%
              </Typography>
            </Box>
            {usagePercentage >= 80 && usagePercentage < 100 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                You are approaching your license limit. Consider purchasing additional licenses.
              </Alert>
            )}
            {usagePercentage >= 100 && (
              <Alert severity="error" sx={{ mt: 1 }}>
                License limit reached. You cannot add more active members until you purchase additional licenses.
              </Alert>
            )}
          </Box>

          {licenseData &&
            (licenseData.planType === 'STANDARD' || licenseData.planType === 'PROFESSIONAL') &&
            licenseData.displayPlan !== 'CANCELLED' &&
            licenseData.displayPlan !== 'EXPIRED' && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddLicense}
              >
                Add License
              </Button>
            </Box>
          )}

          {history && history.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2 }}>
                History
              </Typography>
              <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
                {history.map((h) => (
                  <Box
                    key={h.id}
                    sx={{
                      py: 1.5,
                      px: { xs: 1, sm: 1.5 },
                      borderRadius: 1,
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 260px' },
                        alignItems: 'start',
                        gap: { xs: 1, sm: 2 },
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Licenses
                          </Typography>
                          <Chip
                            size="small"
                            label={`${h.previousCount} → ${h.newCount}`}
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                          <Chip
                            size="small"
                            color={h.delta >= 0 ? 'success' : 'error'}
                            label={h.delta > 0 ? `+${h.delta}` : `${h.delta}`}
                            sx={{ fontWeight: 700 }}
                          />
                        </Box>
                        {h.reason && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {h.reason.replace(/:cs_(?:test|live)_[A-Za-z0-9]+$/, '')}
                          </Typography>
                        )}
                      </Box>
                      <Box
                        sx={{
                          textAlign: { xs: 'left', sm: 'right' },
                          minWidth: 0,
                          pl: { xs: 0, sm: 2 },
                          borderLeft: { xs: 'none', sm: '1px solid' },
                          borderColor: { xs: 'transparent', sm: 'divider' },
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                          {h.changedByUserName || 'Unknown'}
                        </Typography>
                        {h.changedByUserEmail && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={h.changedByUserEmail}
                          >
                            {h.changedByUserEmail}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {formatAppTimestamp(h.changedAt)}
                        </Typography>
                      </Box>
                    </Box>
                    <Divider sx={{ mt: 1.5 }} />
                  </Box>
                ))}
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* License Purchase Summary Dialog (only when subscription is active & plan supports extra licenses) */}
      {licenseData &&
        (licenseData.planType === 'STANDARD' || licenseData.planType === 'PROFESSIONAL') &&
        licenseData.displayPlan !== 'CANCELLED' &&
        licenseData.displayPlan !== 'EXPIRED' && (
        <Dialog
          open={purchaseDialogOpen && licenseCount > 0}
          onClose={handleResetLicenseCount}
          maxWidth={false}
          sx={sidebarAwareDialogSx(444)}
        >
          <DialogTitle>License Purchase Summary</DialogTitle>
          <DialogContent dividers sx={{ overflowX: 'hidden' }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Licenses to Add:
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setLicenseCount((prev) => Math.max(1, prev - 1))}
                  disabled={licenseCount <= 1 || purchasing}
                  sx={{ minWidth: 40 }}
                >
                  -
                </Button>
                <Typography variant="h4" color="primary" sx={{ minWidth: 60, textAlign: 'center' }}>
                  {licenseCount}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setLicenseCount((prev) => prev + 1)}
                  disabled={purchasing}
                  sx={{ minWidth: 40 }}
                >
                  +
                </Button>
              </Box>
            </Box>

            {prorationPreview !== null ? (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Original Price:
                  </Typography>
                  <Typography variant="h6">
                    ${prorationPreview.originalPrice.toFixed(2)}
                    {prorationPreview.billingCycle ? ` / ${prorationPreview.billingCycle.toLowerCase()}` : ''}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Remaining Billing Period:
                  </Typography>
                  <Typography variant="h6">
                    {formatRemainingPeriod(prorationPreview.remainingBillingPeriodDays)}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Prorated Price per License:
                  </Typography>
                  <Typography variant="h6">
                    ${prorationPreview.proratedPricePerLicense.toFixed(2)}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2, pb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Total Cost:
                  </Typography>
                  <Typography variant="h5" color="primary" sx={{ fontWeight: 600 }}>
                    ${prorationPreview.totalAmount.toFixed(2)}
                  </Typography>
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading prorated pricing...
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Button onClick={handleResetLicenseCount} disabled={purchasing}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleProceedWithLicenseCount}
              disabled={purchasing || prorationPreview === null || loadingPrice}
            >
              {purchasing ? <CircularProgress size={20} /> : 'Proceed to Purchase'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default Licenses;

